// src/pages/WhyUs.jsx
//
// Standalone page for the full "Why Choose Us" content - moved off the
// homepage (which now shows only a compact 3-point preview, see
// components/sections/WhyUsPreview.jsx) to keep the homepage short. Every
// section here is an existing, already CMS-driven component relocated
// from Home.jsx, not new hardcoded content - see each import for its real
// Admin > Homepage tab.
import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ArrowRight } from "lucide-react";
import Meta from "../Meta";
import Reveal from "../components/Reveal";
import WhyChooseUs from "../components/Value/WhyChooseUs";
import ThermalInspectionSection from "../components/Solar/ThermalInspectionSection";
import Stats from "../components/sections/Stats";
import Process from "../components/sections/Process";
import Testimonials from "../components/sections/Testimonials";
import FAQ from "../components/sections/FAQ";
import CTA from "../components/sections/CTA";
import { usePageContent } from "../lib/usePageContent";
import { useCms } from "../lib/useCms";
import { copy } from "../lib/copy";
import { useSiteSettings } from "../lib/useSiteSettings";

const DEFAULT_INTRO = "Drone-powered inspection, professional cleaning and real proof — everything that sets us apart from a standard cleaning crew.";

export default function WhyUs() {
  const settings = useSiteSettings();
  const page = usePageContent("why-us");
  const heading = page?.heading || `Why Choose ${settings.name}?`;
  const intro = page?.intro || DEFAULT_INTRO;
  const seoTitle = page?.seoTitle || `Why Choose ${settings.name} | Drone Solar Cleaning Sydney`;
  const seoDesc = page?.seoDescription || `Drone thermal inspection, a 12+ point solar health check, and same-day professional cleaning — see what sets ${settings.name} apart across Sydney.`;

  // Same real-CMS-questions-or-fallback rule the FAQ section itself
  // follows, so this schema always matches exactly what FAQ.jsx renders
  // below on this page - moved here from Home.jsx along with the visible
  // FAQ section it was describing.
  const { data: apiFaqs } = useCms("/api/faqs", null);
  const faqItems = apiFaqs?.length ? apiFaqs.map((f) => ({ q: f.question, a: f.answer })) : copy.faq.items;
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://horizonsolar.com.au/" },
      { "@type": "ListItem", position: 2, name: "Why Us", item: "https://horizonsolar.com.au/why-us" },
    ],
  };

  return (
    <>
      <Meta title={seoTitle} desc={seoDesc} path="/why-us">
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Meta>

      <main className="pt-24 bg-[#02060c]">
        {/* Page header + breadcrumb */}
        <section className="relative overflow-hidden py-16 md:py-20 border-b border-white/5">
          <div className="relative max-w-4xl mx-auto px-6 text-center">
            <nav aria-label="breadcrumb" className="flex items-center justify-center gap-1.5 text-sm text-white/40 mb-6">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-white/70">Why Us</span>
            </nav>
            <span className="text-[#22d3ee] tracking-widest text-sm font-semibold uppercase">Why Horizon</span>
            <h1 className="mt-3 text-4xl md:text-6xl font-extrabold text-white tracking-tight leading-tight">{heading}</h1>
            <p className="mt-4 text-lg text-white/60 max-w-2xl mx-auto">{intro}</p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 rounded-full px-7 py-3.5 bg-[#22d3ee] text-black font-semibold hover:bg-white transition-colors"
            >
              Get a Free Quote
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        <Reveal><WhyChooseUs /></Reveal>
        <Reveal><ThermalInspectionSection /></Reveal>
        <Reveal><Stats /></Reveal>
        <Reveal><Process /></Reveal>
        <Reveal><Testimonials /></Reveal>
        <Reveal><FAQ /></Reveal>
        <Reveal><CTA /></Reveal>
      </main>
    </>
  );
}
